<script setup>
import { ref, computed, defineExpose } from 'vue';
import { useStore } from 'vuex';
import { useAlert } from 'dashboard/composables';
import SzContactsImportAPI from 'dashboard/api/szContactsImport';

import SzImportSidebar from './SzImportSidebar.vue';
import SzStepUpload from './steps/SzStepUpload.vue';
import SzStepPreview from './steps/SzStepPreview.vue';
import SzStepLabels from './steps/SzStepLabels.vue';
import SzStepReview from './steps/SzStepReview.vue';

const emit = defineEmits(['imported']);
const store = useStore();

const open = ref(false);
const currentStep = ref(1);
const isSubmitting = ref(false);

// State shared across steps
const file = ref(null);
const parsedRows = ref([]);
const parseErrors = ref({ invalid: [], missing: [], duplicated: [] });
const totalRows = ref(0);
const customAttributes = ref([]);

const selectedLabelIds = ref([]);
const newLabels = ref([]);

const importId = ref(null);
const importStatus = ref('pending');
const processed = ref(0);
const failedRecordsUrl = ref(null);
const errorMessage = ref(null);
let pollTimer = null;

const steps = computed(() => [
  { key: 'upload',  title: 'Upload de planilha',     subtitle: 'Faça upload da planilha preenchida',     required: true },
  { key: 'preview', title: 'Pré-visualizar contatos', subtitle: 'Pré-visualize os contatos que foram importados', required: true },
  { key: 'labels',  title: 'Etiquetas',              subtitle: 'Selecione ou crie novas etiquetas',      required: false },
  { key: 'review',  title: 'Resumo do envio',        subtitle: 'Verifique os dados antes de importar',   required: true },
]);

const validRows = computed(() =>
  parsedRows.value.filter(r => !(r.errors || []).length)
);
// Hard blockers — invalid format / missing required fields. Duplicates are
// reported as a soft warning (rows are skipped during import, not rejected).
const hasErrors = computed(() =>
  parseErrors.value.invalid.length > 0 || parseErrors.value.missing.length > 0
);
const hasDuplicates = computed(() => parseErrors.value.duplicated.length > 0);

function reset() {
  currentStep.value = 1;
  file.value = null;
  parsedRows.value = [];
  parseErrors.value = { invalid: [], missing: [], duplicated: [] };
  totalRows.value = 0;
  customAttributes.value = [];
  selectedLabelIds.value = [];
  newLabels.value = [];
  importId.value = null;
  importStatus.value = 'pending';
  processed.value = 0;
  failedRecordsUrl.value = null;
  errorMessage.value = null;
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
}

function show()  { reset(); open.value = true; }
function hide()  { open.value = false; reset(); }

async function onFileSelected(picked) {
  file.value = picked;
  try {
    const { data } = await SzContactsImportAPI.upload(picked);
    parsedRows.value = data.rows;
    parseErrors.value = data.errors;
    totalRows.value = data.total;
    customAttributes.value = data.custom_attributes || [];
  } catch (err) {
    parsedRows.value = [];
    parseErrors.value = { invalid: [], missing: [], duplicated: [] };
    useAlert(err?.response?.data?.fatal || 'Falha ao processar a planilha.');
  }
}

function next() {
  if (currentStep.value === 1 && (hasErrors.value || !parsedRows.value.length)) return;
  if (currentStep.value < steps.value.length) currentStep.value += 1;
}
function prev() {
  if (currentStep.value > 1) currentStep.value -= 1;
}
function skipStep() {
  if (currentStep.value === 3) {
    selectedLabelIds.value = [];
    newLabels.value = [];
    currentStep.value = 4;
  }
}

async function submit() {
  if (isSubmitting.value) return;
  isSubmitting.value = true;
  try {
    const { data } = await SzContactsImportAPI.commit({
      rows: validRows.value,
      labelIds: selectedLabelIds.value,
      newLabels: newLabels.value,
    });
    importId.value = data.import_id;
    importStatus.value = data.status;
    startPolling();
  } catch (err) {
    useAlert(err?.response?.data?.error || 'Falha ao iniciar a importação.');
  } finally {
    isSubmitting.value = false;
  }
}

function startPolling() {
  if (pollTimer) clearInterval(pollTimer);
  pollTimer = setInterval(async () => {
    if (!importId.value) return;
    try {
      const { data } = await SzContactsImportAPI.status(importId.value);
      importStatus.value = data.status;
      processed.value = data.processed || 0;
      failedRecordsUrl.value = data.failed_records_url;
      errorMessage.value = data.error_message || null;
      if (['completed', 'failed'].includes(data.status)) {
        clearInterval(pollTimer);
        pollTimer = null;
        emit('imported');
        if (data.status === 'completed') {
          useAlert(`Importação concluída: ${data.processed} contatos.`);
          // refresh contacts list (calls existing vuex action)
          store.dispatch('contacts/get', { page: 1 });
        }
      }
    } catch (_) {
      // transient — keep polling
    }
  }, 2000);
}

defineExpose({ show, hide });
</script>

<template>
  <Teleport to="body">
    <div v-if="open" class="sz-import-overlay" @click.self="hide">
      <div class="sz-import-modal" role="dialog" aria-modal="true">
        <header class="sz-import-header">
          <h3>Importar contatos em massa</h3>
          <button
            type="button"
            class="sz-import-close"
            aria-label="Fechar"
            title="Fechar"
            @click="hide"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round">
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </header>

        <div class="sz-import-body">
          <SzImportSidebar
            :steps="steps"
            :current="currentStep"
            :has-errors="hasErrors"
          />

          <main class="sz-import-content">
            <SzStepUpload
              v-show="currentStep === 1"
              :file="file"
              :errors="parseErrors"
              :total="totalRows"
              :has-blocking-errors="hasErrors"
              @file-selected="onFileSelected"
              @clear="reset"
            />
            <SzStepPreview
              v-show="currentStep === 2"
              :rows="parsedRows"
              :custom-attributes="customAttributes"
            />
            <SzStepLabels
              v-show="currentStep === 3"
              v-model:selected-ids="selectedLabelIds"
              v-model:new-labels="newLabels"
            />
            <SzStepReview
              v-show="currentStep === 4"
              :total="totalRows"
              :valid-rows="validRows"
              :selected-label-ids="selectedLabelIds"
              :new-labels="newLabels"
              :import-id="importId"
              :import-status="importStatus"
              :processed="processed"
              :failed-records-url="failedRecordsUrl"
              :error-message="errorMessage"
              :skipped-duplicates="parseErrors.duplicated.length"
            />
          </main>
        </div>

        <footer class="sz-import-footer">
          <button class="sz-btn sz-btn-secondary" type="button" @click="prev" :disabled="currentStep === 1">
            Voltar
          </button>
          <div class="sz-import-footer-right">
            <button
              v-if="currentStep === 3"
              class="sz-btn sz-btn-secondary"
              type="button"
              @click="skipStep"
            >
              Pular passo &rsaquo;
            </button>
            <button
              v-if="currentStep < 4"
              class="sz-btn sz-btn-primary"
              type="button"
              :disabled="(currentStep === 1 && (hasErrors || !parsedRows.length))"
              @click="next"
            >
              Próximo
            </button>
            <button
              v-else-if="!importId"
              class="sz-btn sz-btn-primary"
              type="button"
              :disabled="isSubmitting"
              @click="submit"
            >
              {{ isSubmitting ? 'Enviando…' : 'Finalizar' }}
            </button>
            <button
              v-else
              class="sz-btn sz-btn-primary"
              type="button"
              @click="hide"
            >
              Fechar
            </button>
          </div>
        </footer>
      </div>
    </div>
  </Teleport>
</template>

<style lang="scss" scoped>
.sz-import-overlay {
  position: fixed; inset: 0;
  background: rgba(0,0,0,0.42);
  backdrop-filter: blur(6px);
  -webkit-backdrop-filter: blur(6px);
  z-index: 100000;
  display: flex; align-items: center; justify-content: center;
  padding: 24px;
}
.sz-import-modal {
  width: 100%;
  max-width: 1040px;
  max-height: 90vh;
  background: #ffffff;
  color: #18181b;
  border-radius: 14px;
  box-shadow: 0 20px 60px rgba(0,0,0,0.32);
  display: flex; flex-direction: column;
  overflow: hidden;
  font-family: inherit;
}


.sz-import-header {
  padding: 18px 22px 12px;
  border-bottom: 1px solid rgba(0,0,0,0.06);
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.sz-import-header h3 {
  margin: 0; font-size: 16px; font-weight: 600;
}

.sz-import-close {
  width: 32px; height: 32px;
  border: 0; background: transparent;
  color: #525252; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
  border-radius: 6px; padding: 0;
  transition: background 0.12s, color 0.12s;
  flex-shrink: 0;
}
.sz-import-close:hover {
  background: rgba(0,0,0,0.06);
  color: #18181b;
}
.dark .sz-import-close,
body.dark .sz-import-close { color: #a1a1aa; }
.dark .sz-import-close:hover,
body.dark .sz-import-close:hover {
  background: rgba(255,255,255,0.06);
  color: #fafafa;
}

.sz-import-body {
  flex: 1; min-height: 0;
  display: grid; grid-template-columns: 280px 1fr;
  overflow: hidden;
}
.sz-import-content {
  padding: 22px; overflow: auto;
}

.sz-import-footer {
  padding: 14px 22px;
  border-top: 1px solid rgba(0,0,0,0.06);
  display: flex; align-items: center; justify-content: space-between;
}

.sz-import-footer-right {
  display: inline-flex; gap: 10px;
}

.sz-btn {
  padding: 9px 18px; border-radius: 10px;
  font-size: 13px; font-weight: 500; cursor: pointer;
  border: 1px solid rgba(0,0,0,0.12);
  background: #ffffff; color: #18181b;
  transition: background 0.15s, border-color 0.15s, opacity 0.15s;
  font-family: inherit;
}
.sz-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.sz-btn:hover:not(:disabled) { background: rgba(0,0,0,0.04); }
.sz-btn-primary {
  background: #16a34a; border-color: #16a34a; color: #ffffff;
}
.sz-btn-primary:hover:not(:disabled) {
  background: #15803d; border-color: #15803d;
}



</style>
